import {
  IndianRupee,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  BadgeCheck,
  BarChart2,
  Store,
  Percent,
  Clock,
  FileText,
} from "lucide-react";
import { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Switch from "@radix-ui/react-switch";
import { useCommissionPayments, MONTHS, YEARS, STATUS_OPTIONS } from "../../hooks/useCommissionPayments";
import { downloadCommissionInvoice } from "../../store/slices/adminReportSlice";
import { showApiError, showApiSuccess } from "../../utils/toast";
import AdminFilterBar from "../../components/ui/AdminFilterBar";
import Pagination from "../../components/tables/Pagination";
import ReusableModal from "../../components/ReusableModal";

// ─── helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const fmtDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const num = (v) => (v ?? 0).toLocaleString("en-IN");

// ─── sub-components ──────────────────────────────────────────────────────────

function PaidBadge({ isPaid }) {
  return isPaid ? (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-300">
      <BadgeCheck className="h-3.5 w-3.5" />
      Paid
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800 border border-red-300">
      <XCircle className="h-3.5 w-3.5" />
      Unpaid
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, iconBg, textColor }) {
  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm flex items-start space-x-3">
      <div className={`${iconBg} p-2.5 rounded-lg flex-shrink-0`}>
        <Icon className={`h-5 w-5 ${textColor}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">{label}</p>
        <p className={`text-lg font-bold ${textColor} mt-0.5`}>{value}</p>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function CommissionPayments() {
  const dispatch = useDispatch();
  const { invoiceLoadingMap } = useSelector((state) => state.adminReport);

  const {
    payments,
    paymentsPagination,
    paymentsFilter,
    paymentsLoading,
    paymentsError,
    summary,
    summaryLoading,
    filters,
    updateFilter,
    handlePageChange,
    resetFilters,
    dropdowns,
    toggleLoadingMap,
    handleTogglePayment,
    refreshData,
    selectedMonthLabel,
  } = useCommissionPayments();

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, uuid: null, isPaid: false, businessName: "" });

  const openConfirmModal = (uuid, isPaid, businessName) => {
    setConfirmModal({ open: true, uuid, isPaid, businessName });
  };

  const closeConfirmModal = () => {
    setConfirmModal({ open: false, uuid: null, isPaid: false, businessName: "" });
  };

  const handleConfirmToggle = () => {
    handleTogglePayment(confirmModal.uuid, confirmModal.isPaid);
    closeConfirmModal();
  };

  const handleDownloadInvoice = useCallback(async (businessUuid) => {
    const result = await dispatch(downloadCommissionInvoice({
      businessUuid,
      month: filters.month,
      year: filters.year,
    }));
    if (downloadCommissionInvoice.fulfilled.match(result)) {
      showApiSuccess("Invoice downloaded successfully");
    } else {
      showApiError(null, result.payload || "Invoice not available for this period");
    }
  }, [dispatch, filters.month, filters.year]);

  // ── derived summary data ───────────────────────────────────────────────────
  const totals = summary?.totals || {};
  const revenueBreakdown = summary?.revenue_breakdown || {};
  const collectionRate = totals.total_amount > 0
    ? Math.round((totals.paid_amount / totals.total_amount) * 100)
    : 0;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <IndianRupee className="h-6 w-6 sm:h-7 sm:w-7 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Commission Payments</h1>
              <p className="text-xs sm:text-sm text-gray-500">Manage monthly commission settlements for premium businesses</p>
            </div>
          </div>
          <button
            onClick={refreshData}
            disabled={paymentsLoading}
            className="self-end sm:self-auto p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${paymentsLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && !summaryLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          <KpiCard icon={Store}       label="Total Businesses"  value={num(totals.total_businesses)}  iconBg="bg-gray-100"    textColor="text-gray-700" />
          <KpiCard icon={BadgeCheck}  label="Paid"              value={num(totals.paid_businesses)}   iconBg="bg-green-100"   textColor="text-green-600" />
          <KpiCard icon={XCircle}     label="Unpaid"            value={num(totals.unpaid_businesses)} iconBg="bg-red-100"     textColor="text-red-600" />
          <KpiCard icon={IndianRupee} label="Amount Collected"  value={fmt(totals.paid_amount)}       iconBg="bg-emerald-100" textColor="text-emerald-600" />
          <KpiCard icon={Clock}       label="Amount Pending"    value={fmt(totals.unpaid_amount)}     iconBg="bg-yellow-100"  textColor="text-yellow-600" />
          <KpiCard icon={Percent}     label="Collection Rate"   value={`${collectionRate}%`}          iconBg="bg-blue-100"    textColor="text-blue-600" />
        </div>
      )}

      {/* Filters + Table merged */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-gray-200">
          <AdminFilterBar
            filters={filters}
            onFilterChange={updateFilter}
            onReset={resetFilters}
            dropdowns={dropdowns}
            showFields={["year", "month", "status", "district", "taluka", "category", "sub_category", "search"]}
            statusOptions={STATUS_OPTIONS}
            searchPlaceholder="Business name or email…"
          />
        </div>

        {/* Table header info */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
          <h2 className="text-sm font-semibold text-gray-700">
            Settlements
            {paymentsFilter && (
              <span className="ml-1 font-normal text-gray-500">
                — {paymentsFilter.month_name} {paymentsFilter.year}
              </span>
            )}
          </h2>
          {paymentsPagination && (
            <span className="text-xs text-gray-500">{paymentsPagination.total} businesses</span>
          )}
        </div>

        {/* Table content */}
        {paymentsLoading ? (
          <div className="flex items-center justify-center py-16 gap-3 text-gray-500">
            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            <span>Loading settlements…</span>
          </div>
        ) : paymentsError ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="h-10 w-10 text-red-400" />
            <p className="text-gray-600">Failed to load settlements.</p>
            <button onClick={refreshData} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
              Try Again
            </button>
          </div>
        ) : payments.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <BarChart2 className="h-12 w-12 text-gray-300" />
            <p className="text-gray-500 font-medium">
              No commission data for {selectedMonthLabel} {filters.year}
            </p>
            <p className="text-gray-400 text-sm">Try a different month, year, or filter combination.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {["#", "Business Name", "Contact No", "District", "Taluka", "Category", "Total Revenue", "Commission", "Paid At", "Status", "Action"].map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {payments.map((row, idx) => {
                  const biz        = row.business || {};
                  const fin        = row.financials || {};
                  const settlement = row.settlement;
                  const uuid       = settlement?.uuid;
                  const isPaid     = settlement?.is_paid ?? false;
                  const isToggling = uuid ? (toggleLoadingMap[uuid] ?? false) : false;

                  return (
                    <tr key={uuid || idx} className={`transition-colors ${
                      isPaid
                        ? "bg-green-100 hover:bg-green-200"
                        : "bg-red-100 hover:bg-red-200"
                    }`}>
                      <td className="px-4 py-3 text-gray-400 font-mono text-xs">
                        {(paymentsPagination
                          ? (paymentsPagination.current_page - 1) * paymentsPagination.per_page
                          : 0) + idx + 1}
                      </td>

                      <td className="px-4 py-3">
                        <p className="font-semibold text-gray-900 whitespace-nowrap">{biz.business_name}</p>
                      </td>

                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {Array.isArray(biz.contact_numbers) && biz.contact_numbers.length > 0
                          ? biz.contact_numbers[0]
                          : "—"}
                      </td>

                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {biz.district?.name || "—"}
                      </td>

                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {biz.taluka?.name || "—"}
                      </td>

                      <td className="px-4 py-3 text-gray-600 text-xs whitespace-nowrap">
                        {biz.category?.name || "—"}
                      </td>

                      <td className="px-4 py-3 font-semibold text-gray-900 whitespace-nowrap">
                        {fmt(fin.total_revenue)}
                      </td>

                      <td className="px-4 py-3 font-bold text-orange-600 whitespace-nowrap">
                        {fmt(fin.total_commission)}
                      </td>

                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {fmtDate(settlement?.paid_at)}
                      </td>

                      <td className="px-4 py-3">
                        {settlement ? <PaidBadge isPaid={isPaid} /> : (
                          <span className="text-xs text-gray-400">No record</span>
                        )}
                      </td>

                      <td className="px-4 py-3">
                        {settlement ? (
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                              <Switch.Root
                                checked={isPaid}
                                onCheckedChange={() => openConfirmModal(uuid, isPaid, biz.business_name)}
                                disabled={isToggling}
                                className="w-[42px] h-[25px] bg-gray-200 rounded-full relative shadow-sm data-[state=checked]:bg-green-600 outline-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Switch.Thumb className="block w-[21px] h-[21px] bg-white rounded-full transition-transform translate-x-0.5 will-change-transform data-[state=checked]:translate-x-[19px]" />
                              </Switch.Root>
                              {isToggling ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                              ) : (
                                <span className={`text-xs font-semibold ${isPaid ? "text-green-700" : "text-gray-500"}`}>
                                  {isPaid ? "Paid" : "Unpaid"}
                                </span>
                              )}
                            </div>
                            {isPaid && (
                              <button
                                onClick={() => handleDownloadInvoice(biz.uuid)}
                                disabled={invoiceLoadingMap[biz.uuid]}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100 transition-colors disabled:opacity-50"
                              >
                                <FileText className={`h-3.5 w-3.5 ${invoiceLoadingMap[biz.uuid] ? "animate-pulse" : ""}`} />
                                {invoiceLoadingMap[biz.uuid] ? "Downloading…" : "Receipt"}
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {paymentsPagination && paymentsPagination.last_page > 1 && (
          <Pagination
            currentPage={paymentsPagination.current_page}
            totalPages={paymentsPagination.last_page}
            totalItems={paymentsPagination.total}
            itemsPerPage={paymentsPagination.per_page}
            onPageChange={handlePageChange}
          />
        )}
      </div>

      {/* Confirmation Modal */}
      <ReusableModal
        open={confirmModal.open}
        onOpenChange={(open) => !open && closeConfirmModal()}
        type="confirm"
        title={confirmModal.isPaid ? "Mark as Unpaid" : "Mark as Paid"}
        message={`Are you sure you want to mark the commission for "${confirmModal.businessName}" as ${confirmModal.isPaid ? "unpaid" : "paid"}?`}
        confirmText={confirmModal.isPaid ? "Yes, Mark Unpaid" : "Yes, Mark Paid"}
        onConfirm={handleConfirmToggle}
        onCancel={closeConfirmModal}
      />
    </div>
  );
}
